import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Tabs,
    Tab,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Snackbar,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    Card,
    CardContent,
    CardActions,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import EditIcon from '@mui/icons-material/EditRounded';
import AddIcon from '@mui/icons-material/AddRounded';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSendRounded';
import SyncIcon from '@mui/icons-material/SyncRounded';
import EmailIcon from '@mui/icons-material/EmailRounded';
import GroupIcon from '@mui/icons-material/GroupRounded';
import SettingsIcon from '@mui/icons-material/SettingsRounded';
import SaveIcon from '@mui/icons-material/SaveRounded';
import { SurfaceCard } from '../components/ui/SurfaceCard';
import { useResponsive } from '../hooks/useResponsive';

interface DistributionList {
    groupName: string;
    recipients: string[];
    enabled: boolean;
    createdAt: number;
    updatedAt: number;
}

interface ReportHeader {
    groupName: string;
    headerHtml: string;
    introText: string;
    subjectLine: string;
    enabled: boolean;
    createdAt: number;
    updatedAt: number;
}

interface ReportSettings {
    id: string;
    cronSchedule: string;
    enabledGroups: string[];
    senderEmail: string;
    defaultSubject: string;
    replyToEmail?: string;
    updatedAt: number;
    updatedBy?: string;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

interface ReportSettingsViewProps {
    onBack: () => void;
}

/**
 * Card text actions sit flush with the card content: no horizontal padding, so the
 * start icon lines up with the text above (MUI's -2px icon margin offsets the glyph's
 * own inset). Hover lifts the colour instead of drawing a background pill.
 */
const cardTextActionSx = {
    px: 0,
    minWidth: 0,
    '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
};

export const ReportSettingsView: React.FC<ReportSettingsViewProps> = ({ onBack }) => {
    const { isPhone } = useResponsive();
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    
    // Data states
    const [distributionLists, setDistributionLists] = useState<DistributionList[]>([]);
    const [reportHeaders, setReportHeaders] = useState<ReportHeader[]>([]);
    const [settings, setSettings] = useState<ReportSettings | null>(null);
    const [cognitoGroups, setCognitoGroups] = useState<string[]>([]);
    
    // UI states
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ 
        open: false, message: '', severity: 'success' 
    });
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingList, setEditingList] = useState<DistributionList | null>(null);
    const [newRecipient, setNewRecipient] = useState('');
    const [groupUsers, setGroupUsers] = useState<{ email: string; username: string }[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // Fetch all data on mount
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [listsRes, headersRes, settingsRes, groupsRes] = await Promise.all([
                fetch('/api/report-config/distribution-lists'),
                fetch('/api/report-config/headers'),
                fetch('/api/report-config/settings'),
                fetch('/api/reports/groups')
            ]);

            if (listsRes.ok) setDistributionLists(await listsRes.json());
            if (headersRes.ok) setReportHeaders(await headersRes.json());
            if (settingsRes.ok) setSettings(await settingsRes.json());
            if (groupsRes.ok) setCognitoGroups(await groupsRes.json());
        } catch (error) {
            console.error('Error fetching data:', error);
            setSnackbar({ open: true, message: 'Failed to load configuration', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Sync Cognito groups
    const handleSyncCognito = async () => {
        setSyncing(true);
        try {
            const res = await fetch('/api/report-config/sync-cognito', { method: 'POST' });
            const data = await res.json();
            if (res.ok) {
                setSnackbar({ open: true, message: `Synced ${data.synced} new groups`, severity: 'success' });
                fetchData();
            } else {
                throw new Error(data.error);
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to sync groups', severity: 'error' });
        } finally {
            setSyncing(false);
        }
    };

    // Save distribution list
    const handleSaveDistributionList = async (list: DistributionList) => {
        setSaving(true);
        try {
            // Clean up recipients: remove DISABLED: prefix and filter out disabled ones
            const cleanedList = {
                ...list,
                recipients: list.recipients
                    .filter(email => !email.startsWith('DISABLED:'))
                    .map(email => email.replace('DISABLED:', ''))
            };
            
            const res = await fetch('/api/report-config/distribution-lists', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(cleanedList)
            });
            if (res.ok) {
                setSnackbar({ open: true, message: 'Distribution list saved', severity: 'success' });
                fetchData();
                setEditDialogOpen(false);
                setEditingList(null);
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to save distribution list', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // Load users when editing a distribution list
    useEffect(() => {
        if (editDialogOpen && editingList?.groupName) {
            setLoadingUsers(true);
            fetch(`/api/reports/groups/${editingList.groupName}/users`)
                .then(res => res.json())
                .then(data => {
                    if (Array.isArray(data)) {
                        setGroupUsers(data);
                    }
                })
                .catch(console.error)
                .finally(() => setLoadingUsers(false));
        } else {
            setGroupUsers([]);
        }
    }, [editDialogOpen, editingList?.groupName]);

    // Save report header
    const handleSaveReportHeader = async (header: ReportHeader) => {
        setSaving(true);
        try {
            const res = await fetch('/api/report-config/headers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(header)
            });
            if (res.ok) {
                setSnackbar({ open: true, message: 'Header configuration saved', severity: 'success' });
                fetchData();
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to save header', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // Save global settings
    const handleSaveSettings = async () => {
        if (!settings) return;
        setSaving(true);
        try {
            const res = await fetch('/api/report-config/settings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(settings)
            });
            if (res.ok) {
                setSnackbar({ open: true, message: 'Settings saved', severity: 'success' });
                fetchData();
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to save settings', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // Toggle list enabled status
    const handleToggleListEnabled = async (list: DistributionList) => {
        const updated = { ...list, enabled: !list.enabled };
        await handleSaveDistributionList(updated);
    };

    // Add recipient to editing list
    const handleAddRecipient = () => {
        if (!editingList || !newRecipient.includes('@')) return;
        if (editingList.recipients.includes(newRecipient)) return;
        setEditingList({
            ...editingList,
            recipients: [...editingList.recipients, newRecipient]
        });
        setNewRecipient('');
    };

    // Remove recipient from editing list
    const handleRemoveRecipient = (email: string) => {
        if (!editingList) return;
        setEditingList({
            ...editingList,
            recipients: editingList.recipients.filter(r => r !== email)
        });
    };

    return (
        <Box>
            <SurfaceCard padding="none" radius="lg" tint="#CDA075">
                {/* Header: the same structure as the Manual report card ("Send report"): icon chip, title
                    and subtitle, with the card's own action on the right. */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.75,
                        px: { xs: 2, sm: 3 },
                        pt: { xs: 2.25, sm: 3 },
                        // The tab bar below closes the header (its hairline is the divider).
                        pb: { xs: 1, sm: 1.5 },
                    }}
                >
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '14px',
                            display: 'grid',
                            placeItems: 'center',
                            flexShrink: 0,
                            color: '#F1D3AF',
                            bgcolor: 'rgba(205,160,117,0.14)',
                            border: '1px solid rgba(205,160,117,0.3)',
                        }}
                    >
                        <ScheduleSendIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography component="h2" sx={{ fontSize: { xs: '1.0625rem', sm: '1.1875rem' }, fontWeight: 700, letterSpacing: '-0.01em' }}>
                            Automatic reports
                        </Typography>
                        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mt: 0.25 }}>
                            Manage distribution lists, email templates, and scheduling
                        </Typography>
                    </Box>
                    <Tooltip title="Sync with Cognito groups">
                        {/* Span: the tooltip still works while the button is disabled (syncing). */}
                        <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
                            {isPhone ? (
                                // Phones: a 44px icon button, so the title keeps its line.
                                <IconButton
                                    aria-label="Sync groups"
                                    onClick={handleSyncCognito}
                                    disabled={syncing}
                                    sx={{ width: 44, height: 44, border: '1px solid rgba(255,255,255,0.14)', bgcolor: 'rgba(255,255,255,0.04)' }}
                                >
                                    {syncing ? <CircularProgress size={18} sx={{ color: '#E5BF95' }} /> : <SyncIcon sx={{ color: '#E5BF95' }} />}
                                </IconButton>
                            ) : (
                                <Button
                                    variant="outlined"
                                    startIcon={syncing ? <CircularProgress size={16} sx={{ color: '#E5BF95' }} /> : <SyncIcon sx={{ color: '#E5BF95' }} />}
                                    onClick={handleSyncCognito}
                                    disabled={syncing}
                                >
                                    Sync groups
                                </Button>
                            )}
                        </Box>
                    </Tooltip>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Tabs
                            value={tabValue}
                            onChange={(_, v) => setTabValue(v)}
                            variant={isPhone ? "fullWidth" : "standard"}
                            scrollButtons={false}
                            sx={{
                                // Second-level navigation: underlined tabs (the pill tabs above are the first level).
                                px: { xs: 1, sm: 2.5 },
                                minHeight: 0,
                                borderBottom: '1px solid rgba(255,255,255,0.08)',
                                '& .MuiTabs-flexContainer': { gap: { xs: 0, sm: 2 } },
                                '& .MuiTab-root': {
                                    minHeight: 48,
                                    px: { xs: 1, sm: 0.5 },
                                    minWidth: 0,
                                    gap: 0.75,
                                    fontWeight: 650,
                                    textTransform: 'none',
                                    fontSize: '0.8125rem',
                                    '& .MuiTab-iconWrapper': { m: 0, fontSize: 17 },
                                    '&.Mui-selected': { color: 'text.primary' },
                                },
                                '& .MuiTabs-indicator': { backgroundColor: '#CDA075', height: 2, borderRadius: 2 },
                            }}
                        >
                            <Tab icon={<GroupIcon sx={{ fontSize: 17 }} />} label={isPhone ? 'Lists' : 'Distribution lists'} iconPosition="start" disableRipple />
                            <Tab icon={<EmailIcon sx={{ fontSize: 17 }} />} label={isPhone ? 'Headers' : 'Email headers'} iconPosition="start" disableRipple />
                            <Tab icon={<SettingsIcon sx={{ fontSize: 17 }} />} label={isPhone ? 'Settings' : 'Email configuration'} iconPosition="start" disableRipple />
                        </Tabs>

                        {/* Tab 0: Distribution Lists */}
                        <TabPanel value={tabValue} index={0}>
                            <Box sx={{ px: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <Box sx={{ width: 4, height: 20, bgcolor: '#E5BF95', borderRadius: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Configure which recipients receive automated reports for each group
                                    </Typography>
                                </Box>
                                <Grid container spacing={2}>
                                    {cognitoGroups.map(groupName => {
                                        const list = distributionLists.find(l => l.groupName === groupName);
                                        return (
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={groupName}>
                                                <Card
                                                    variant="outlined"
                                                    sx={{
                                                        height: '100%',
                                                        // Flex column + mt: auto on the actions: "Edit" / "Configure" sit on one
                                                        // line across a row of cards, whatever each card holds.
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        bgcolor: list?.enabled ? 'rgba(205,160,117,0.05)' : 'rgba(0,0,0,0.28)',
                                                        borderColor: list?.enabled ? 'rgba(205,160,117,0.3)' : 'rgba(255,255,255,0.1)',
                                                        borderRadius: '18px',
                                                        transition: 'border-color 0.2s ease, transform 0.2s ease',
                                                        '&:hover': {
                                                            borderColor: list?.enabled ? '#E5BF95' : 'rgba(255,255,255,0.18)',
                                                            transform: 'translateY(-2px)'
                                                        }
                                                    }}
                                                >
                                                    <CardContent sx={{ pb: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                                            <Typography variant="h6" fontWeight="600" sx={{ color: '#FFFFFF' }}>
                                                                {groupName}
                                                            </Typography>
                                                            <Switch
                                                                checked={list?.enabled ?? false}
                                                                onChange={() => list && handleToggleListEnabled(list)}
                                                                size="small"
                                                                disabled={!list}
                                                            />
                                                        </Box>
                                                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1.5 }}>
                                                            {list ? `${list.recipients.length} recipients` : 'Not configured'}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {list && list.recipients.slice(0, 2).map(email => (
                                                                <Chip 
                                                                    key={email} 
                                                                    label={email.split('@')[0]} 
                                                                    size="small" 
                                                                    sx={{ 
                                                                        bgcolor: 'rgba(255,255,255,0.08)', 
                                                                        color: 'rgba(255,255,255,0.78)',
                                                                        border: 'none',
                                                                        fontSize: '0.75rem'
                                                                    }} 
                                                                />
                                                            ))}
                                                            {list && list.recipients.length > 2 && (
                                                                <Chip 
                                                                    label={`+${list.recipients.length - 2}`} 
                                                                    size="small" 
                                                                    sx={{ 
                                                                        bgcolor: 'rgba(255,255,255,0.08)', 
                                                                        color: '#CDA075',
                                                                        border: 'none',
                                                                        fontWeight: 600,
                                                                        fontSize: '0.75rem'
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </CardContent>
                                                    <CardActions sx={{ mt: 'auto', pt: 0, pb: 2, px: 2 }}>
                                                        <Button
                                                            size="small"
                                                            startIcon={<EditIcon />}
                                                            onClick={() => {
                                                                setEditingList(list || {
                                                                    groupName,
                                                                    recipients: [],
                                                                    enabled: true,
                                                                    createdAt: Date.now(),
                                                                    updatedAt: Date.now()
                                                                });
                                                                setEditDialogOpen(true);
                                                            }}
                                                            sx={cardTextActionSx}
                                                        >
                                                            {list ? 'Edit' : 'Configure'}
                                                        </Button>
                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        </TabPanel>

                        {/* Tab 1: Email Headers */}
                        <TabPanel value={tabValue} index={1}>
                            <Box sx={{ px: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <Box sx={{ width: 4, height: 20, bgcolor: 'rgba(255,255,255,0.72)', borderRadius: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Configure custom headers, intro text, and subject lines for automated reports
                                    </Typography>
                                </Box>

                                <Grid container spacing={2}>
                                    {cognitoGroups.map(groupName => {
                                        const header = reportHeaders.find(h => h.groupName === groupName);
                                        return (
                                            <Grid size={{ xs: 12, md: 6 }} key={groupName}>
                                                <Card 
                                                    variant="outlined"
                                                    sx={{
                                                        bgcolor: 'rgba(0,0,0,0.28)',
                                                        borderColor: 'rgba(255,255,255,0.1)',
                                                        borderRadius: 2
                                                    }}
                                                >
                                                    <CardContent>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                            <Typography variant="h6" fontWeight="600" sx={{ color: '#FFFFFF' }}>
                                                                {groupName}
                                                            </Typography>
                                                            <Switch
                                                                checked={header?.enabled ?? false}
                                                                onChange={() => {
                                                                    const updated = header ? { ...header, enabled: !header.enabled } : {
                                                                        groupName,
                                                                        headerHtml: '',
                                                                        introText: '',
                                                                        subjectLine: '',
                                                                        enabled: true,
                                                                        createdAt: Date.now(),
                                                                        updatedAt: Date.now()
                                                                    };
                                                                    handleSaveReportHeader(updated);
                                                                }}
                                                                size="small"
                                                            />
                                                        </Box>
                                                
                                                        <TextField
                                                            label="Subject Line"
                                                            fullWidth
                                                            size="small"
                                                            value={header?.subjectLine ?? ''}
                                                            sx={{ 
                                                                mb: 2,
                                                                '& .MuiOutlinedInput-root': {
                                                                    bgcolor: 'rgba(0,0,0,0.32)',
                                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.24)' }
                                                                }
                                                            }}
                                                            placeholder="TeksCapital - Weekly Report"
                                                            disabled
                                                        />
                                                
                                                        <TextField
                                                            label="Intro Text (for automated reports)"
                                                            fullWidth
                                                            size="small"
                                                            multiline
                                                            rows={3}
                                                            value={header?.introText ?? ''}
                                                            placeholder="Weekly performance summary..."
                                                            disabled
                                                            sx={{ 
                                                                '& .MuiOutlinedInput-root': {
                                                                    bgcolor: 'rgba(0,0,0,0.32)',
                                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.24)' }
                                                                }
                                                            }}
                                                        />
                                                    </CardContent>
                                                    <CardActions sx={{ px: 2, pb: 2 }}>
                                                        <Button
                                                            size="small"
                                                            startIcon={<SaveIcon />}
                                                            onClick={() => {
                                                                const toSave = header || {
                                                                    groupName,
                                                                    headerHtml: '',
                                                                    introText: '',
                                                                    subjectLine: '',
                                                                    enabled: true,
                                                                    createdAt: Date.now(),
                                                                    updatedAt: Date.now()
                                                                };
                                                                handleSaveReportHeader(toSave);
                                                            }}
                                                            disabled={saving}
                                                            sx={cardTextActionSx}
                                                        >
                                                            Save
                                                        </Button>
                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        </TabPanel>

                        {/* Tab 2: Automated Report Settings */}
                        <TabPanel value={tabValue} index={2}>
                            <Box sx={{ px: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <Box sx={{ width: 4, height: 20, bgcolor: '#CDA075', borderRadius: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Configure global settings for automated weekly reports
                                    </Typography>
                                </Box>

                                <Grid container spacing={3}>
                                    {/* Left Column */}
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Paper sx={{ 
                                            p: 3, 
                                            mb: 3, 
                                            bgcolor: 'rgba(0,0,0,0.28)', 
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: 2
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                <SettingsIcon sx={{ color: 'rgba(255,255,255,0.72)', fontSize: 20 }} />
                                                <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#FFFFFF' }}>
                                                    Schedule
                                                </Typography>
                                            </Box>
                                            <TextField
                                                label="Cron Schedule"
                                                fullWidth
                                                size="small"
                                                value={settings?.cronSchedule ?? '0 9 * * 1'}
                                                onChange={(e) => setSettings(prev => prev ? { ...prev, cronSchedule: e.target.value } : null)}
                                                helperText="Default: 0 9 * * 1 (Monday at 9:00 AM)"
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: 'rgba(0,0,0,0.32)',
                                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.24)' },
                                                        '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.72)' }
                                                    },
                                                    '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.6)' }
                                                }}
                                            />
                                    
                                            <Alert 
                                                severity="info" 
                                                sx={{ 
                                                    bgcolor: 'rgba(255,255,255,0.04)', 
                                                    border: '1px solid rgba(255,255,255,0.10)',
                                                    color: 'text.primary',
                                                    '& .MuiAlert-icon': { color: 'text.secondary' },
                                                    '& code': { 
                                                        bgcolor: 'rgba(0,0,0,0.3)', 
                                                        px: 0.5, 
                                                        borderRadius: 0.5,
                                                        fontFamily: 'monospace'
                                                    }
                                                }}
                                            >
                                                Common schedules: <br />
                                                • <code>0 9 * * 1</code> - Every Monday at 9:00 AM<br />
                                                • <code>0 9 * * 5</code> - Every Friday at 9:00 AM<br />
                                                • <code>0 9 1 * *</code> - 1st of every month at 9:00 AM
                                            </Alert>
                                        </Paper>

                                        <Paper sx={{ 
                                            p: 3, 
                                            bgcolor: 'rgba(0,0,0,0.28)', 
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: 2
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                <EmailIcon sx={{ color: '#E5BF95', fontSize: 20 }} />
                                                <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#FFFFFF' }}>
                                                    Email Settings
                                                </Typography>
                                            </Box>
                                    
                                            <TextField
                                                label="Sender Email"
                                                fullWidth
                                                size="small"
                                                value={settings?.senderEmail ?? ''}
                                                onChange={(e) => setSettings(prev => prev ? { ...prev, senderEmail: e.target.value } : null)}
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: 'rgba(0,0,0,0.32)',
                                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.24)' },
                                                        '&.Mui-focused fieldset': { borderColor: '#E5BF95' }
                                                    }
                                                }}
                                            />
                                    
                                            <TextField
                                                label="Reply-To Email (optional)"
                                                fullWidth
                                                size="small"
                                                value={settings?.replyToEmail ?? ''}
                                                onChange={(e) => setSettings(prev => prev ? { ...prev, replyToEmail: e.target.value } : null)}
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: 'rgba(0,0,0,0.32)',
                                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.24)' },
                                                        '&.Mui-focused fieldset': { borderColor: '#E5BF95' }
                                                    }
                                                }}
                                            />
                                    
                                            <TextField
                                                label="Default Subject Line"
                                                fullWidth
                                                size="small"
                                                value={settings?.defaultSubject ?? ''}
                                                onChange={(e) => setSettings(prev => prev ? { ...prev, defaultSubject: e.target.value } : null)}
                                                sx={{ 
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: 'rgba(0,0,0,0.32)',
                                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.24)' },
                                                        '&.Mui-focused fieldset': { borderColor: '#E5BF95' }
                                                    }
                                                }}
                                            />
                                        </Paper>
                                    </Grid>

                                    {/* Right Column */}
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Paper sx={{ 
                                            p: 3, 
                                            bgcolor: 'rgba(0,0,0,0.28)', 
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: 2
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                <GroupIcon sx={{ color: '#CDA075', fontSize: 20 }} />
                                                <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#FFFFFF' }}>
                                                    Enabled Groups for Automated Reports
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>
                                                Select which groups should receive automated weekly reports.
                                            </Typography>
                                    
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Enabled Groups</InputLabel>
                                                <Select
                                                    multiple
                                                    value={settings?.enabledGroups ?? []}
                                                    onChange={(e) => setSettings(prev => prev ? { 
                                                        ...prev, 
                                                        enabledGroups: typeof e.target.value === 'string' 
                                                            ? e.target.value.split(',') 
                                                            : e.target.value 
                                                    } : null)}
                                                    label="Enabled Groups"
                                                    renderValue={(selected) => (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {selected.map((value) => (
                                                                <Chip 
                                                                    key={value} 
                                                                    label={value} 
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: 'rgba(255,255,255,0.08)',
                                                                        color: '#FFFFFF',
                                                                        border: 'none'
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    )}
                                                    sx={{
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: 'rgba(255,255,255,0.12)'
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: 'rgba(255,255,255,0.24)'
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#CDA075'
                                                        },
                                                        bgcolor: 'rgba(0,0,0,0.32)'
                                                    }}
                                                >
                                                    {cognitoGroups.map((group) => (
                                                        <MenuItem key={group} value={group}>
                                                            {group}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        startIcon={saving ? <CircularProgress size={16} sx={{ color: 'rgba(0,0,0,0.6)' }} /> : <SaveIcon />}
                                        onClick={handleSaveSettings}
                                        disabled={saving}
                                        sx={{ px: 3.5 }}
                                    >
                                        Save settings
                                    </Button>
                                </Box>
                            </Box>
                        </TabPanel>
                    </>
                )}
            </SurfaceCard>

            {/* Edit Distribution List Dialog */}
            <Dialog 
                open={editDialogOpen} 
                onClose={() => setEditDialogOpen(false)} 
                maxWidth="sm" 
                fullWidth

            >
                <DialogTitle sx={{ 
                    background: 'linear-gradient(135deg, rgba(205,160,117,0.16) 0%, rgba(255,255,255,0) 70%)',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25
                }}>
                    <GroupIcon sx={{ color: '#F1D3AF' }} />
                    Edit Distribution: {editingList?.groupName}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ mt: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={editingList?.enabled ?? true}
                                    onChange={(e) => setEditingList(prev => prev ? { ...prev, enabled: e.target.checked } : null)}
                                />
                            }
                            label={<Typography sx={{ color: 'rgba(255,255,255,0.78)' }}>Enable automated reports for this group</Typography>}
                            sx={{ mb: 2 }}
                        />
                        
                        <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

                        {/* Suggested Users from Cognito Group */}
                        {groupUsers.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <Box sx={{ width: 3, height: 14, bgcolor: 'rgba(255,255,255,0.72)', borderRadius: 1 }} />
                                    <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                        Suggested from {editingList?.groupName}
                                    </Typography>
                                </Box>
                                {loadingUsers ? (
                                    <CircularProgress size={20} sx={{ color: '#E5BF95' }} />
                                ) : (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {groupUsers
                                            .filter(u => !editingList?.recipients.includes(u.email))
                                            .map(user => (
                                                <Chip
                                                    key={user.email}
                                                    label={user.email}
                                                    size="small"
                                                    icon={<AddIcon sx={{ fontSize: 16 }} />}
                                                    onClick={() => {
                                                        if (editingList && !editingList.recipients.includes(user.email)) {
                                                            setEditingList({
                                                                ...editingList,
                                                                recipients: [...editingList.recipients, user.email]
                                                            });
                                                        }
                                                    }}
                                                    sx={{ 
                                                        cursor: 'pointer',
                                                        bgcolor: 'rgba(255,255,255,0.06)',
                                                        color: 'rgba(255,255,255,0.72)',
                                                        border: '1px solid rgba(255,255,255,0.16)',
                                                        '&:hover': { 
                                                            bgcolor: 'rgba(255,255,255,0.12)',
                                                            borderColor: 'rgba(255,255,255,0.72)'
                                                        },
                                                        '& .MuiChip-icon': { color: 'rgba(255,255,255,0.72)' }
                                                    }}
                                                />
                                            ))
                                        }
                                        {groupUsers.filter(u => !editingList?.recipients.includes(u.email)).length === 0 && (
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                                All group users have been added
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )}

                        <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Box sx={{ width: 3, height: 14, bgcolor: '#E5BF95', borderRadius: 1 }} />
                            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                Recipients ({editingList?.recipients.length ?? 0})
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <TextField
                                size="small"
                                placeholder="email@example.com"
                                value={newRecipient}
                                onChange={(e) => setNewRecipient(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddRecipient()}
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: 'rgba(0,0,0,0.28)',
                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.24)' },
                                        '&.Mui-focused fieldset': { borderColor: '#E5BF95' }
                                    }
                                }}
                            />
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleAddRecipient}
                                disabled={!newRecipient.includes('@')}
                                sx={{
                                    borderColor: 'rgba(205,160,117,0.4)',
                                    color: '#E5BF95',
                                    '&:hover': {
                                        borderColor: '#E5BF95',
                                        bgcolor: 'rgba(205,160,117,0.1)'
                                    }
                                }}
                            >
                                Add
                            </Button>
                        </Box>
                        
                        <List dense sx={{ 
                            maxHeight: 200, 
                            overflow: 'auto', 
                            bgcolor: 'rgba(0,0,0,0.28)', 
                            borderRadius: 2,
                            border: '1px solid rgba(255,255,255,0.08)'
                        }}>
                            {editingList?.recipients.map(email => {
                                const isEnabled = !email.startsWith('DISABLED:');
                                const displayEmail = isEnabled ? email : email.replace('DISABLED:', '');
                                return (
                                    <ListItem key={email} sx={{ 
                                        py: 1,
                                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                                        '&:last-child': { borderBottom: 'none' }
                                    }}>
                                        <Switch
                                            size="small"
                                            checked={isEnabled}
                                            onChange={() => {
                                                if (!editingList) return;
                                                const newRecipients = editingList.recipients.map(r => {
                                                    if (r === email) {
                                                        return isEnabled ? `DISABLED:${displayEmail}` : displayEmail;
                                                    }
                                                    return r;
                                                });
                                                setEditingList({ ...editingList, recipients: newRecipients });
                                            }}
                                            sx={{ mr: 1 }}
                                        />
                                        <ListItemText 
                                            primary={displayEmail} 
                                            sx={{ 
                                                '& .MuiListItemText-primary': {
                                                    color: isEnabled ? '#FFFFFF' : 'rgba(255,255,255,0.6)',
                                                    textDecoration: isEnabled ? 'none' : 'line-through'
                                                }
                                            }}
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton 
                                                edge="end" 
                                                size="small" 
                                                onClick={() => handleRemoveRecipient(email)}
                                                sx={{ 
                                                    color: 'rgba(255,255,255,0.6)',
                                                    '&:hover': { color: '#FF7070', bgcolor: 'rgba(255,112,112,0.1)' }
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                );
                            })}
                            {editingList?.recipients.length === 0 && (
                                <ListItem>
                                    <ListItemText 
                                        primary="No recipients added" 
                                        secondary="Click on a suggested user or add an email manually"
                                        sx={{ 
                                            textAlign: 'center',
                                            '& .MuiListItemText-primary': { color: 'rgba(255,255,255,0.6)' },
                                            '& .MuiListItemText-secondary': { color: 'rgba(255,255,255,0.38)' }
                                        }}
                                    />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <Button 
                        onClick={() => setEditDialogOpen(false)}
                        sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="contained"
                        color="secondary"
                        onClick={() => editingList && handleSaveDistributionList(editingList)}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} sx={{ color: 'rgba(0,0,0,0.6)' }} /> : <SaveIcon />}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ReportSettingsView;
